/**
 * Navigation Configuration
 * Single source of truth for all navigation items in the application
 *
 * Structure:
 * - primary: Bottom navigation on mobile, main navigation items (4 items)
 * - secondary: Hamburger menu items (3 items)
 *
 * Each item includes:
 * - id: Unique identifier
 * - label: Display text
 * - icon: Material-UI icon name
 * - route: React Router path
 * - description: Tooltip/help text
 * - keywords: Search terms for command palette
 * - subitems: Optional nested navigation (for Capture)
 * - adminOnly: Optional flag for admin-only items
 */

export const NAVIGATION_CONFIG = {
  primary: [
    {
      id: 'insights',
      label: 'Insights',
      icon: 'InsightsIcon',
      route: '/dashboard',
      description: 'View analytics, metrics, and organizational insights',
      keywords: ['dashboard', 'analytics', 'metrics', 'insights', 'data', 'charts', 'reports'],
    },
    {
      id: 'care',
      label: 'Care',
      icon: 'VolunteerActivismIcon',
      route: '/care',
      description: 'Connected Community Model - curated resources for capacity, care, capital, and community',
      keywords: ['care', 'ccm', 'community', 'capacity', 'capital', 'resources', 'connections', 'network'],
    },
    {
      id: 'capture',
      label: 'Capture',
      icon: 'CreateIcon',
      route: '/capture',
      description: 'Capture information through audio, uploads, or notes',
      keywords: ['capture', 'create', 'add', 'input', 'record', 'upload', 'note'],
      subitems: [
        {
          id: 'audio',
          label: 'Audio',
          icon: 'MicIcon',
          route: '/capture/audio',
          description: 'Record and transcribe audio conversations',
          keywords: ['audio', 'record', 'mic', 'voice', 'transcribe', 'speech'],
        },
        {
          id: 'upload',
          label: 'Upload',
          icon: 'UploadFileIcon',
          route: '/capture/upload',
          description: 'Upload documents and files for processing',
          keywords: ['upload', 'file', 'document', 'import', 'pdf', 'csv'],
        },
        {
          id: 'notebook',
          label: 'Notebook',
          icon: 'MenuBookIcon',
          route: '/capture/notebook',
          description: 'Create rich text notes and documentation',
          keywords: ['notebook', 'notes', 'write', 'document', 'text', 'editor'],
        },
      ],
    },
    {
      id: 'chat',
      label: 'Faana AI',
      icon: 'SmartToyIcon',
      route: '/chat',
      description: 'AI-powered chat assistant for insights and actions',
      keywords: ['chat', 'ai', 'assistant', 'ask', 'help', 'conversation', 'bot', 'faana'],
    },
    {
      id: 'entities',
      label: 'Entities',
      icon: 'AccountTreeIcon',
      route: '/entities',
      description: 'Manage organizations, people, programs, and communities',
      keywords: ['entities', 'clients', 'organizations', 'people', 'management', 'relationships'],
    },
  ],

  secondary: [
    {
      id: 'faana-agents',
      label: 'Faana Agents',
      icon: 'PsychologyIcon',
      route: '/faana-agents',
      description: 'Manage and configure AI agents',
      keywords: ['agents', 'ai', 'automation', 'bots', 'faana', 'configure'],
    },
    {
      id: 'behavioral-workbench',
      label: 'Behavioral Workbench',
      icon: 'PsychologyAltIcon',
      route: '/behavioral-workbench',
      description: 'Label documents with behavioral codes for analysis',
      keywords: ['behavioral', 'workbench', 'coding', 'labels', 'analysis', 'faana', 'dimensions'],
    },
    {
      id: 'data-sources',
      label: 'Data Sources',
      icon: 'StorageIcon',
      route: '/data-management',
      description: 'Manage data sources and integrations (Admin only)',
      keywords: ['data', 'sources', 'integrations', 'bigquery', 'storage', 'admin'],
      adminOnly: true,
    },
    {
      id: 'help',
      label: 'Help',
      icon: 'HelpIcon',
      route: '/help',
      description: 'Documentation, tutorials, and support',
      keywords: ['help', 'support', 'documentation', 'tutorial', 'guide', 'faq'],
    },
  ],
};

/**
 * Utility function to get all navigation items (primary + secondary)
 */
export const getAllNavigationItems = () => {
  return [...NAVIGATION_CONFIG.primary, ...NAVIGATION_CONFIG.secondary];
};

/**
 * Utility function to get navigation item by ID
 */
export const getNavigationItemById = (id) => {
  const allItems = getAllNavigationItems();

  // Check top-level items
  const topLevel = allItems.find(item => item.id === id);
  if (topLevel) return topLevel;

  // Check subitems
  for (const item of allItems) {
    if (item.subitems) {
      const subitem = item.subitems.find(sub => sub.id === id);
      if (subitem) return subitem;
    }
  }

  return null;
};

/**
 * Utility function to get navigation item by route
 */
export const getNavigationItemByRoute = (route) => {
  const allItems = getAllNavigationItems();

  // Check top-level items
  const topLevel = allItems.find(item => item.route === route);
  if (topLevel) return topLevel;

  // Check subitems
  for (const item of allItems) {
    if (item.subitems) {
      const subitem = item.subitems.find(sub => sub.route === route);
      if (subitem) return subitem;
    }
  }

  return null;
};

/**
 * Utility function to search navigation items by keyword
 */
export const searchNavigationItems = (query) => {
  const allItems = getAllNavigationItems();
  const searchTerm = query.toLowerCase();
  const results = [];

  for (const item of allItems) {
    // Search in label, description, and keywords
    const matches =
      item.label.toLowerCase().includes(searchTerm) ||
      item.description.toLowerCase().includes(searchTerm) ||
      item.keywords.some(keyword => keyword.includes(searchTerm));

    if (matches) {
      results.push(item);
    }

    // Search subitems
    if (item.subitems) {
      for (const subitem of item.subitems) {
        const subMatches =
          subitem.label.toLowerCase().includes(searchTerm) ||
          subitem.description.toLowerCase().includes(searchTerm) ||
          subitem.keywords.some(keyword => keyword.includes(searchTerm));

        // eslint-disable-next-line max-depth
        if (subMatches) {
          results.push({ ...subitem, parentLabel: item.label });
        }
      }
    }
  }

  return results;
};
